import { LitElement, css, html, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { shared } from '../styles/shared.js';

/**
 * Star-burst overlay played for each correct answer. CSS-only animation; no
 * external animation library needed at this scale.
 *
 * Setting `show=true` plays the animation and auto-clears after ~800 ms,
 * dispatching `reward-done`.
 */
@customElement('rewards-overlay')
export class RewardsOverlay extends LitElement {
  static override styles = [
    shared,
    css`
      :host {
        position: fixed;
        inset: 0;
        pointer-events: none;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 80;
      }

      .burst {
        position: relative;
        width: 0;
        height: 0;
      }

      .star {
        position: absolute;
        font-size: 96px;
        line-height: 1;
        transform-origin: center;
        animation: star-fly var(--motion-medium) var(--easing-bounce) forwards;
      }

      .star.s0 {
        transform: translate(-200px, 0);
      }
      .star.s1 {
        transform: translate(0, -160px);
      }
      .star.s2 {
        transform: translate(200px, 0);
      }
      .star.s3 {
        transform: translate(-130px, 130px);
      }
      .star.s4 {
        transform: translate(130px, 130px);
      }

      @keyframes star-fly {
        0% {
          transform: translate(0, 0) scale(0.2) rotate(-30deg);
          opacity: 0;
        }
        40% {
          opacity: 1;
        }
        100% {
          transform: var(--end-transform) scale(1.2) rotate(20deg);
          opacity: 0;
        }
      }
    `,
  ];

  @property({ type: Boolean, reflect: true }) show = false;

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('show') && this.show) {
      window.setTimeout(() => {
        this.show = false;
        this.dispatchEvent(new CustomEvent('reward-done', { bubbles: true, composed: true }));
      }, 800);
    }
  }

  override render() {
    if (!this.show) return null;
    return html`
      <div class="burst" aria-hidden="true">
        ${[0, 1, 2, 3, 4].map(
          (i) =>
            html`<span
              class=${`star s${i}`}
              style="--end-transform: translate(${this.endX(i)}px, ${this.endY(i)}px)"
              >⭐</span
            >`,
        )}
      </div>
    `;
  }

  private endX(i: number): number {
    return [-200, 0, 200, -130, 130][i] ?? 0;
  }
  private endY(i: number): number {
    return [0, -160, 0, 130, 130][i] ?? 0;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rewards-overlay': RewardsOverlay;
  }
}
